import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import '../styles/CaseStudyDetail.css';

const SwiggyCaseStudy = () => {
  const navigate = useNavigate();

  const handleBackToPortfolio = () => {
    navigate('/', { state: { scrollTo: 'work' } });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Hide header on scroll down, show on scroll up
  useEffect(() => {
    let lastScrollY = window.scrollY;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const header = document.querySelector('.detail-header');
      
      if (!header) return;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down & past threshold
        header.classList.add('hidden');
      } else {
        // Scrolling up
        header.classList.remove('hidden');
      }
      
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="case-study-detail">
      {/* Header */}
      <header className="detail-header">
        <div className="detail-header-content">
          <button onClick={handleBackToPortfolio} className="back-button">
            <ArrowLeft size={20} />
            <span>Go back</span>
          </button>
        </div>
      </header>

      {/* Hero Video */}
      <section className="detail-hero">
        <video 
          src="https://customer-assets.emergentagent.com/job_portfolio-resume-28/artifacts/c4of3v95_Screen%20Recording%202026-04-30%20at%205.54.19%E2%80%AFPM.mov"
          className="hero-video"
          autoPlay
          loop
          muted
          playsInline
        />
      </section>

      {/* Behance Link */}
      <div className="behance-link-wrapper">
        <a 
          href="https://www.behance.net/gallery/169000483/Swiggy-Group-Ordering-Case-Study" 
          target="_blank" 
          rel="noopener noreferrer"
          className="behance-link"
        >
          View full case study <ExternalLink size={14} />
        </a>
      </div>

      {/* Content */}
      <section className="detail-content">
        <div className="content-container">
          
          <h1 className="project-title">Swiggy Group Ordering</h1>
          <p className="project-note">Done prior to Swiggy introducing group ordering</p>
          <p className="project-subtitle">Making coordinated food ordering with friends seamless, social, and delightful.</p>

          {/* Article Content */}
          <div className="article-content-compact">
            
            {/* 01 Context & Problem Discovery */}
            <div className="cs-section">
              <h2 className="cs-section-title">CONTEXT & PROBLEM DISCOVERY</h2>
              
              <p>Personal project exploring how food delivery apps could better serve group ordering scenarios: office lunches, friend gatherings, family dinners. These situations happen frequently but existing ordering flows treat them as edge cases rather than core experiences.</p>

              <h3 className="cs-subsection-headline">The problem</h3>
              <p>Ordering food as a group feels like project management. Through interviews and observation, I identified recurring pain points: screenshot sharing chaos across WhatsApp, Slack, text threads; coordination confusion with no clarity on who ordered what or if the cart is final; payment hassle of chasing people for money after delivery; timing mismatches where some people are ready immediately while others are still browsing 30 minutes later; and complete lack of shared cart visibility.</p>
            </div>

            {/* 02 My Role */}
            <div className="cs-section">
              <h2 className="cs-section-title">MY ROLE</h2>
              
              <p>Solo personal project handling all aspects: user research through interviews and observational studies, competitive analysis of existing group ordering features, information architecture and user flow design, interaction design and prototyping, and visual design maintaining Swiggy's brand system.</p>
            </div>

            {/* 03 Design Approach & Key Decisions */}
            <div className="cs-section">
              <h2 className="cs-section-title">DESIGN APPROACH & KEY DECISIONS</h2>
              
              <h3 className="cs-subsection-headline">Map the natural flow</h3>
              <p>Designed the experience to mirror how groups actually decide what to eat: initiation (one person suggests), browsing (everyone explores independently), selection (add items with clear ownership), review (see totals), checkout (each pays individually).</p>

              <h3 className="cs-subsection-headline">Individual payment, not group payment</h3>
              <p>Initial designs had one person pay upfront and others reimburse later. This came up repeatedly in research as the biggest friction point. Built individual payment directly into checkout. Order is placed when everyone has paid, eliminating post-delivery money collection entirely.</p>

              <h3 className="cs-subsection-headline">Shareable links over in-app invites</h3>
              <p>In-app invites would require everyone to have Swiggy installed and be logged in before joining. Web-based shareable links let anyone join without barriers, creating an account only at checkout if needed. Lower barrier to participation.</p>

              <h3 className="cs-subsection-headline">Individual item ownership</h3>
              <p>Early concepts had a single shared cart where anyone could edit anything. User testing revealed this created anxiety ("What if someone accidentally deletes my order?"). Each person owns their items and can only edit their own selections. Clear visual attribution (color-coded names) shows who ordered what.</p>
            </div>

            {/* 04 Solution */}
            <div className="cs-section">
              <h2 className="cs-section-title">SOLUTION</h2>
              
              <h3 className="cs-subsection-headline">Create or join group orders</h3>
              <p>Host selects restaurant and creates group order. Generates shareable link friends can join instantly.</p>

              <h3 className="cs-subsection-headline">Real-time cart visibility</h3>
              <p>All participants see shared cart updating in real-time. Clear attribution shows who ordered what. Status badges indicate who's browsing, who's ready, who's paid.</p>

              <h3 className="cs-subsection-headline">Individual item ownership</h3>
              <p>Each person owns and controls only their items. No one can accidentally delete or modify someone else's order.</p>

              <h3 className="cs-subsection-headline">Automatic payment splitting</h3>
              <p>At checkout, each person pays only for their items through integrated UPI or card payment. No manual calculations, no post-delivery collection hassles.</p>

              <h3 className="cs-subsection-headline">Group order tracking</h3>
              <p>Everyone gets real-time updates on order status throughout the delivery journey. No more "where's the food?" messages flooding the organizer.</p>
            </div>

            {/* 05 Impact */}
            <div className="cs-section">
              <h2 className="cs-section-title">IMPACT</h2>
              
              <p>As a concept project, these are expected outcomes based on research and usability testing:</p>
              
              <ul className="cs-list-compact">
                <li>Eliminates coordination friction: no more message chains or "did you order yet?" loops</li>
                <li>Clear ownership: everyone knows their items and costs upfront</li>
                <li>No payment collection hassle</li>
                <li>Real-time transparency lets everyone make informed decisions</li>
              </ul>

              <p>Swiggy has since introduced their own group ordering feature, validating the problem space.</p>
            </div>

            {/* 06 What I Learnt */}
            <div className="cs-section cs-section-last">
              <h2 className="cs-section-title">WHAT I LEARNT</h2>
              
              <p>Group ordering isn't just about food. It's about coordination, trust, and creating shared experiences together.</p>

              <p>The design decisions that had the biggest impact weren't flashy. They were small details: real-time updates, individual payment, clear ownership indicators. These removed friction points users had learned to accept as "just how it is."</p>

              <p>Good UX research doesn't require a big budget. Just curiosity and access to people experiencing the problem. Observing a single office lunch coordination revealed more insights than any survey could.</p>
            </div>

          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="detail-footer">
        <button onClick={handleBackToPortfolio} className="back-to-portfolio">
          <ArrowLeft size={18} />
          Go back
        </button>
      </footer>
    </div>
  );
};

export default SwiggyCaseStudy;
